// The ScaleManager is deprecated; you should instead directly use the
// purely functional ScaleBar, which accepts a total range and a visible
// range and encapsulates all the computations that the ScaleManager
// provides.

import moment from 'moment';
import d3 from 'd3';
import { scaleDiscontinuous } from 'd3fc-discontinuous-scale';
import { clamp } from 'lodash';
import skipWeekends from './DeprecatedDiscontinuitySkipWeekends';
import { startOfFiscalYear } from 'javascripts/util/dateFns';

export default class ScaleManager {
  // Min and max dates hold the maximum extent of the timeline. fromDate and
  // toDate contains the visible portion of the timeline.
  // Width holds the visible width of the chart.
  constructor(options) {
    this.options = { timeScale: null, ...options };
    const { minDate, maxDate, fromDate, toDate } = options;

    this.minDate = moment.max(
      moment.utc(minDate).startOf('day'),
      moment().subtract(20, 'years')
    );

    this.maxDate = moment.min(
      moment.utc(maxDate).endOf('day'),
      moment().add(20, 'years')
    );

    if (fromDate) {
      this.fromDate = moment.utc(fromDate).startOf('day');
    } else {
      this.fromDate = this.minDate.clone();
    }

    if (toDate) {
      this.toDate = moment.utc(toDate).endOf('day');
    } else {
      this.toDate = this.maxDate.clone();
    }

    if (this.fromDate < this.minDate) {
      this.fromDate = this.minDate.clone();
    }

    if (this.toDate > this.maxDate) {
      this.toDate = this.maxDate.clone();
    }

    this.visibleStartDate = moment.utc(this.options.visibleStartDate);
    this.visibleEndDate = moment.utc(this.options.visibleEndDate);
    this.width = this.options.width;
    this.fontSize = this.options.fontSize || 12;

    this.pixelsPerDay = Math.abs(
      this.options.width / this.daysInVisibleRange()
    );

    this.minorInterval =
      this.normalizeTimeScale(
        this.options.timeScale,
        this.pixelsPerDay,
        this.fontSize
      ) || this.minorIntervalForPixels(this.pixelsPerDay, this.fontSize);

    this.majorInterval = this.majorIntervalForMinor(this.minorInterval);

    if (this.options.hideWeekends) {
      this.x = scaleDiscontinuous(d3.time.scale())
        .discontinuityProvider(skipWeekends)
        .domain([this.minDate.toDate(), this.maxDate.toDate()])
        .range([0, this.totalPixelWidth()]);
    } else {
      this.x = d3.time
        .scale()
        .range([0, this.totalPixelWidth()])
        .domain([this.minDate.toDate(), this.maxDate.toDate()]);
    }
  }

  leftPercentage() {
    return this.dateToPercentage(this.fromDate);
  }

  rightPercentage() {
    return this.dateToPercentage(this.toDate);
  }

  canScroll() {
    return !(
      this.fromDate.isSame(this.minDate) && this.toDate.isSame(this.maxDate)
    );
  }
  canScrollLeft() {
    return !this.fromDate.isSame(this.minDate);
  }
  canScrollRight() {
    return !this.toDate.isSame(this.maxDate);
  }

  normalizeTimeScale(timeScale, pixelsPerDay, fontSize) {
    if (!timeScale || timeScale === 'auto') {
      return null;
    }

    const fontSizeScaler = fontSize / 12;

    if (
      (timeScale === 'months' ||
        timeScale === 'weeks' ||
        timeScale === 'days') &&
      pixelsPerDay < 0.9 * fontSizeScaler
    ) {
      return 'quarters';
    } else if (
      (timeScale === 'weeks' || timeScale === 'days') &&
      pixelsPerDay < 2.1 * fontSizeScaler
    ) {
      return 'months';
    } else if (timeScale === 'days' && pixelsPerDay < 8 * fontSizeScaler) {
      return 'weeks';
    } else {
      return timeScale;
    }
  }

  majorIntervalForMinor(minorInterval) {
    switch (minorInterval) {
      case 'days':
        return 'weeks';
      case 'weeks':
        return 'months';
      case 'months':
        return 'quarters';
      case 'quarters':
        return 'years';
    }
  }

  minorIntervalForPixels(pixelsPerDay, fontSize) {
    // The original values of 13, 3, and 1.1 were obtained experimentally at
    // font size 12. We then simply scale them to try and account for a change
    // in font size
    const fontSizeScaler = fontSize / 12;

    if (pixelsPerDay >= 13 * fontSizeScaler) {
      return 'days';
    } else if (pixelsPerDay > 3 * fontSizeScaler) {
      return 'weeks';
    } else if (pixelsPerDay > 1.1 * fontSizeScaler) {
      return 'months';
    } else {
      return 'quarters';
    }
  }

  dateToOffset(date) {
    // Round to day.
    date = date.clone().startOf('day');
    if (this.options.hideWeekends && date.weekday() === 0) {
      date.weekday(1);
    }
    return Math.round(this.x(date.toDate()));
  }
  dateToOffsetEnd(date) {
    date = date.clone().endOf('day');
    if (this.options.hideWeekends && date.weekday() === 6) {
      date.weekday(5);
    }
    return Math.round(this.x(date.toDate()));
  }

  dateToRawOffset(date) {
    return Math.round(this.x(date.clone().toDate()));
  }

  dateToPercentage(date) {
    let diff;
    if (this.options.hideWeekends) {
      diff = date.businessDiff(this.minDate, 'days');
    } else {
      diff = date.diff(this.minDate, 'days');
    }
    return Math.min(Math.max((100 * diff) / this.daysInTotalRange(), 0), 100);
  }

  isHidden(fromDate, toDate) {
    if (!this.options.hideWeekends) {
      return false;
    }

    const daysBetween = toDate
      .startOf('day')
      .diff(fromDate.startOf('day'), 'days');
    if (daysBetween > 2) {
      return false;
    }

    return (
      [0, 6].includes(toDate.weekday()) && [0, 6].includes(toDate.weekDay())
    );
  }

  percentageToDate(percentage) {
    percentage = Math.min(Math.max(percentage, 0), 100);
    const base = this.minDate.clone();
    const days = Math.round((percentage * this.daysInTotalRange()) / 100.0);
    if (this.options.hideWeekends) {
      return base.businessAdd(days, 'days');
    } else {
      return base.add(days, 'days');
    }
  }

  percentageToOffset(percentage) {
    percentage = clamp(percentage, 0, 1);
    return this.totalPixelWidth() * percentage;
  }

  offsetToDate(offset) {
    const date = moment.utc(this.x.invert(offset)).startOf('day');
    if (!this.options.hideWeekends) {
      return date;
    }
    if (date.weekday() === 6) {
      date.weekday(8);
    }
    return date;
  }
  offsetToDateEnd(offset) {
    return moment.utc(this.x.invert(offset)).endOf('day');
  }

  offsetToPercentage(offset) {
    return offset / this.totalPixelWidth();
  }

  totalPixelWidth() {
    return parseInt(this.pixelsPerDay * this.daysInTotalRange());
  }

  maximumScroll() {
    return this.totalPixelWidth() - this.options.width;
  }

  daysInTotalRange() {
    if (this.options.hideWeekends) {
      return this.maxDate.businessDiff(this.minDate, 'days');
    } else {
      return this.maxDate.diff(this.minDate, 'days');
    }
  }

  daysInVisibleRange() {
    if (this.options.hideWeekends) {
      return this.toDate.businessDiff(this.fromDate, 'days');
    } else {
      return this.toDate.diff(this.fromDate, 'days');
    }
  }

  pixelsPerMinorInterval() {
    switch (this.minorInterval) {
      case 'days':
        return this.pixelsPerDay;
      case 'weeks':
        return this.pixelsPerDay * (this.options.hideWeekends ? 5 : 7);
      case 'months':
        return this.pixelsPerDay * (this.options.hideWeekends ? 21 : 30);
      case 'quarters':
        return this.pixelsPerDay * (this.options.hideWeekends ? 21 : 30) * 3;
    }
  }

  weekends() {
    const dates = [];
    moment
      .utc()
      .range(
        this.minDate.clone().startOf('week').subtract(1, 'day'),
        this.maxDate
      )
      .by('weeks', moment => {
        return dates.push(moment);
      });
    return dates;
  }

  minorIntervals() {
    switch (this.minorInterval) {
      case 'weeks':
        return this.weekInterval();
      case 'months':
        return this.monthInterval();
      case 'quarters':
        return this.quarterInterval();
      default:
        return [];
    }
  }

  weekInterval(dates = []) {
    const start = this.options.hideWeekends ? 'isoweek' : 'week';
    const startDate = this.minDate.clone().startOf(start);

    moment
      .utc()
      .range(startDate, this.maxDate)
      .by('weeks', date => dates.push(date));

    return dates;
  }

  monthInterval(dates = []) {
    moment
      .utc()
      .range(this.minDate.clone().startOf('month'), this.maxDate)
      .by('months', date => dates.push(date));

    return dates;
  }

  quarterInterval(dates = []) {
    moment
      .utc()
      .range(this.minDate.clone().startOf('month'), this.maxDate)
      .by('months', date => {
        if (this.inQuarter(date)) {
          dates.push(date);
        }
      });

    return dates;
  }

  yearInterval(dates = []) {
    moment
      .utc()
      .range(
        moment.utc(
          startOfFiscalYear(this.minDate, {
            fiscalMonth: this.options.fiscalMonth,
          })
        ),
        this.maxDate
      )
      .by('years', date => dates.push(date));

    return dates;
  }

  inQuarter(date) {
    // The fiscalMonth goes from 1 - 12, but date.month() goes from 0 - 11
    const fiscalMonthIndex = this.options.fiscalMonth - 1;
    return (date.month() - fiscalMonthIndex) % 3 === 0;
  }

  majorIntervals() {
    switch (this.majorInterval) {
      case 'weeks':
        return this.weekInterval();
      case 'months':
        return this.monthInterval();
      case 'quarters':
        return this.quarterInterval();
      case 'years':
        return this.yearInterval();
      default:
        return [];
    }
  }
}
